import { isEmpty, unionBy } from 'lodash';
import type { Dispatch, SetStateAction } from 'react';
import type { search } from 'sr-types';
import { FETCH_POLICY_CONFIG, FILTER_IDENTIFIERS } from '../constants/constants';
import { searchClient, slimResultsQuery } from '../list/slimQuery';
import { log } from './commonUtils';

export type SearchDataProps = {
    entity: string;
    activeOrganizationAccount: string;
    from: number;
    batchSize: number;
    filters: Record<string, any>[];
    sortOrder: string;
    includeAllActions: boolean;
    additionalFragmentName?: string;
    signal?: AbortSignal;
    retries?: number;
};

type SearchHits<TItem> = {
    items: TItem[];
    page?: {
        total?: number;
    };
};

export type SearchResponse<TItem> = {
    data?: {
        results?: {
            hits?: SearchHits<TItem>;
            facets?: search.SkFacetSet[];
        };
    };
};

export type PagedSearchPageFailure = {
    pageIndex: number;
    chunkIndex: number;
    pageOffset: number;
    cause: unknown;
};

export class PagedSearchIncompleteError extends Error {
    constructor(readonly entity: string, readonly failures: PagedSearchPageFailure[]) {
        super(`Failed to fetch complete ${entity} search results: ${failures.length} page(s) failed.`);
        this.name = 'PagedSearchIncompleteError';
    }
}

const RETRY_CONFIG = {
    maxBackoffMs: 2000,
    baseBackoffMs: 500,
    jitterMs: 150,
    abortErrorCode: 20
} as const;

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

const isAbortError = (err: unknown): boolean => {
    if (!err || typeof err !== 'object') return false;

    const e = err as {
        name?: string;
        message?: string;
        code?: number;
    };

    return (
        e.name === 'AbortError' ||
        e.message?.toLowerCase().includes('aborted') ||
        e.code === RETRY_CONFIG.abortErrorCode
    );
};

const executeSearchWithRetry = async <T>(
    fn: () => Promise<T>,
    { retries = 2, signal }: { retries?: number; signal?: AbortSignal } = {}
): Promise<T> => {
    let attempt = 0;
    let lastErr: any;

    while (attempt <= retries) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

        try {
            return await fn();
        } catch (err) {
            lastErr = err;

            if (isAbortError(err) || attempt === retries) throw err;

            const backoffMs =
                Math.min(RETRY_CONFIG.maxBackoffMs, RETRY_CONFIG.baseBackoffMs * 2 ** attempt) +
                Math.floor(Math.random() * RETRY_CONFIG.jitterMs);

            await sleep(backoffMs);
            attempt++;
        }
    }

    throw lastErr;
};

export const searchData = async <TItem>({
    entity,
    activeOrganizationAccount,
    from,
    batchSize,
    filters: extraFilters,
    sortOrder,
    includeAllActions,
    additionalFragmentName,
    signal,
    retries = 2
}: SearchDataProps): Promise<SearchResponse<TItem>> => {
    const filters = [
        { identifier: FILTER_IDENTIFIERS.entity, value: entity },
        ...(extraFilters?.filter((f) => !isEmpty(f)) ?? [])
    ];

    return executeSearchWithRetry(
        () =>
            searchClient.query({
                query: slimResultsQuery(entity, includeAllActions, additionalFragmentName),
                variables: {
                    filters,
                    page: {
                        from,
                        size: batchSize
                    },
                    sortBy: sortOrder
                },
                fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
                context: {
                    headers: {
                        ownerId: activeOrganizationAccount
                    },
                    fetchOptions: { signal }
                }
            }) as Promise<SearchResponse<TItem>>,
        { retries, signal }
    );
};

export const searchByPage = async <TItem>(
    entity: string,
    activeOrganizationAccount: string,
    batchSize: number,
    filters: Record<string, any>[],
    sortOrder: string,
    setBatchState: Dispatch<SetStateAction<TItem[]>>,
    setSearchData?: Dispatch<SetStateAction<TItem[]>>,
    chunkSize: number = 2,
    includeAllActions: boolean = false,
    additionalFragmentName?: string,
    signal?: AbortSignal,
    onFirstPageResponse?: (response: SearchResponse<TItem>) => void,
    retries: number = 2,
    requireCompleteResults: boolean = false
): Promise<any[]> => {
    let totalFetched = 0;
    const searchedData: TItem[] = [];
    const start = Date.now();

    const isAborted = (): boolean => Boolean(signal?.aborted);
    if (isAborted()) return [];

    let res: SearchResponse<TItem>;
    try {
        res = await searchData<TItem>({
            entity,
            activeOrganizationAccount,
            from: totalFetched,
            batchSize,
            filters,
            sortOrder,
            includeAllActions,
            additionalFragmentName,
            signal,
            retries
        });
    } catch (error) {
        log('Initial fetch failed after retries:', error);
        if (requireCompleteResults) {
            if (isAbortError(error) || isAborted()) throw error;
            throw new PagedSearchIncompleteError(entity, [{ pageIndex: 0, chunkIndex: 0, pageOffset: 0, cause: error }]);
        }
        return [];
    }

    if (isAborted()) return searchedData;

    onFirstPageResponse?.(res);

    const firstBatchResults: TItem[] = res?.data?.results?.hits?.items ?? [];
    const totalRecords: number = res?.data?.results?.hits?.page?.total ?? 0;

    if (!isEmpty(firstBatchResults)) {
        setSearchData?.((prev) => unionBy([...(Array.isArray(prev) ? prev : []), ...firstBatchResults], 'id'));
        setBatchState(firstBatchResults);

        searchedData.push(...firstBatchResults);
        totalFetched += firstBatchResults.length;
    }

    const totalPages: number = Math.ceil(totalRecords / batchSize);
    let aborted = false;
    const pageFailures: PagedSearchPageFailure[] = [];

    // Process subsequent batches in chunks
    for (let currentPage = 1, chunkIndex = 1; currentPage < totalPages; currentPage += chunkSize, chunkIndex++) {
        if (isAborted()) {
            aborted = true;
            break;
        }

        const batchRequests: Promise<SearchResponse<TItem>>[] = [];

        // create only `chunkSize` requests
        for (let i = 0; i < chunkSize && currentPage + i < totalPages; i++) {
            if (isAborted()) {
                aborted = true;
                break;
            }

            const pageStartIndex = (currentPage + i) * batchSize;

            batchRequests.push(
                searchData({
                    entity,
                    activeOrganizationAccount,
                    from: pageStartIndex,
                    batchSize,
                    filters,
                    sortOrder,
                    includeAllActions,
                    additionalFragmentName,
                    signal,
                    retries
                })
            );
        }

        if (aborted) break;

        const results = await Promise.allSettled(batchRequests);

        if (isAborted()) {
            aborted = true;
            break;
        }

        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                const batchResults: TItem[] = result.value?.data?.results?.hits?.items;
                if (batchResults && !isEmpty(batchResults)) {
                    searchedData.push(...batchResults);
                    totalFetched += batchResults.length;
                }
            } else {
                log('Batch fetch failed after retries:', result.reason);
                const pageIndex = currentPage + index;
                pageFailures.push({ pageIndex, chunkIndex, pageOffset: pageIndex * batchSize, cause: result.reason });
            }
        });
    }

    if (!aborted && !isAborted()) {
        if (requireCompleteResults && pageFailures.length) {
            throw new PagedSearchIncompleteError(entity, pageFailures);
        }
        setBatchState(unionBy(searchedData, 'id'));
        const end = Date.now();
        log(`** All done, Execution time: ${end - start} ms`);
    }
    return searchedData;
};
